import { useRef, useState, type ReactNode } from 'react'
import { urlMedia } from '../apiCatalogue'

/**
 * Image et carrousel.
 *
 * `Image` est le composant qui change le plus la perception de qualité, et c'est aussi celui qui
 * coûte le moins : **les trois déclinaisons existent depuis L2** — 320 / 800 / 1600 px en WebP,
 * ré-encodées sans EXIF par le Worker — et n'étaient servies que par une seule URL. Il n'y a rien
 * à produire côté serveur ; il manquait la façon de les demander.
 *
 * Trois choses, dont aucune n'est décorative :
 *
 *   1. `srcset` + `sizes` : le téléphone télécharge 320 px là où il affichait 1600 px. Sur la 3G
 *      visée par le §13 de la spec, c'est le plus gros gain du lot.
 *   2. Une boîte à **ratio fixe** : la place est réservée avant que l'image arrive, donc la mise
 *      en page ne saute pas. C'est ce qui amène le décalage cumulé sous 0,05.
 *   3. Un fondu à l'arrivée sur fond neutre, plutôt qu'une image qui apparaît d'un coup — par
 *      ANIMATION CSS et non par un état React : une image déjà en cache finit de charger avant
 *      que React n'attache `onLoad`, et resterait alors invisible sans la moindre erreur.
 */

const RATIOS = {
  '1-1': 'image--1-1',
  '4-3': 'image--4-3',
  '3-2': 'image--3-2',
  '16-9': 'image--16-9',
  /* Dans une mosaique, c'est la CASE qui porte la taille : imposer un ratio ici se
     battrait avec la grille, et la grande photo cesserait d'etre grande. */
  remplir: 'image--remplir',
} as const

export function Image({
  mediaId,
  alt,
  ratio = '4-3',
  sizes = '100vw',
  prioritaire = false,
  vide,
  className,
}: {
  mediaId: string | null
  /**
   * Vide sur une photo décorative — la carte entière est déjà un lien qui porte le titre. Décrit
   * partout ailleurs. Un `alt` qui répète le titre juste à côté est du bruit, pas de
   * l'accessibilité.
   */
  alt: string
  ratio?: keyof typeof RATIOS
  /** Indispensable : sans lui, le navigateur suppose 100vw et retélécharge la plus grande. */
  sizes?: string
  /** La première image visible n'est jamais différée : c'est elle que mesure le LCP. */
  prioritaire?: boolean
  vide?: ReactNode
  className?: string
}) {
  return (
    <div className={`image ${RATIOS[ratio]}${className === undefined ? '' : ` ${className}`}`}>
      {mediaId === null ? (
        <span className="image__vide">{vide}</span>
      ) : (
        <img
          src={urlMedia(mediaId, 'card')}
          srcSet={`${urlMedia(mediaId, 'thumbnail')} 320w, ${urlMedia(mediaId, 'card')} 800w, ${urlMedia(mediaId, 'detail')} 1600w`}
          sizes={sizes}
          alt={alt}
          loading={prioritaire ? 'eager' : 'lazy'}
          fetchPriority={prioritaire ? 'high' : undefined}
          decoding="async"
        />
      )}
    </div>
  )
}

/**
 * Carrousel horizontal.
 *
 * `scroll-snap` natif : le geste tactile, la molette et le clavier fonctionnent sans une ligne de
 * JavaScript, et **l'ordre s'inverse seul en arabe**. Une bibliothèque de carrousel apporterait
 * son propre système de direction — précisément l'endroit où le RTL se rétrofite.
 *
 * Le seul rôle du script est de tenir les pastilles à jour au défilement.
 */
export function Carrousel({ children, ariaLabel }: { children: ReactNode[]; ariaLabel: string }) {
  const piste = useRef<HTMLDivElement>(null)
  const [actif, setActif] = useState(0)

  return (
    <div className="carrousel" role="group" aria-label={ariaLabel}>
      <div
        className="carrousel__piste"
        ref={piste}
        onScroll={() => {
          const element = piste.current
          if (element === null || element.clientWidth === 0) {
            return
          }

          // `Math.abs` : en arabe, `scrollLeft` est négatif. Sans lui, la pastille active
          // reste bloquée sur la première photo — un défaut invisible en français.
          setActif(Math.round(Math.abs(element.scrollLeft) / element.clientWidth))
        }}
      >
        {children}
      </div>

      {children.length > 1 && (
        <span className="carrousel__points" aria-hidden="true">
          {children.map((_, rang) => (
            <span key={rang} className={rang === actif ? 'est-actif' : undefined} />
          ))}
        </span>
      )}
    </div>
  )
}
